import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, ArrowLeft, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificacionesCompletas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notificaciones, setNotificaciones] = useState([]);
  const [notificacionesFiltradas, setNotificacionesFiltradas] = useState([]);
  const [searchCedula, setSearchCedula] = useState('');

  useEffect(() => {
    loadNotificaciones();
  }, []);

  useEffect(() => {
    // Filtrar por cédula
    if (searchCedula.trim()) {
      const filtered = notificaciones.filter(n => 
        n.expediente_numero && n.expediente_numero.toLowerCase().includes(searchCedula.toLowerCase())
      );
      setNotificacionesFiltradas(filtered);
    } else {
      setNotificacionesFiltradas(notificaciones);
    }
  }, [searchCedula, notificaciones]);

  const loadNotificaciones = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/notificaciones?solo_no_leidas=true&limit=200`,
        {
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Ordenar por fecha ascendente (FIFO - las más antiguas primero)
        const sorted = data.sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        );
        setNotificaciones(sorted);
        setNotificacionesFiltradas(sorted);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notificacionId, expedienteId) => {
    try {
      // Marcar como leída
      await fetch(
        `${BACKEND_URL}/api/notificaciones/${notificacionId}/marcar-leida`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      // Navegar al expediente
      navigate(`/expediente/${expedienteId}`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Navegar de todas formas
      navigate(`/expediente/${expedienteId}`);
    }
  };

  const formatFecha = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 60) {
      return `Hace ${minutes} min`;
    } else if (hours < 24) {
      return `Hace ${hours}h`;
    } else {
      return date.toLocaleDateString('es-ES', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Cargando notificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-blue-500" />
              <h1 className="text-2xl font-bold text-white">Notificaciones</h1>
              {notificacionesFiltradas.length > 0 && (
                <Badge className="bg-red-500 text-white">
                  {notificacionesFiltradas.length} sin leer
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Buscador */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por número de expediente..."
                  value={searchCedula}
                  onChange={(e) => setSearchCedula(e.target.value)}
                  className="pl-10 bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                />
                {searchCedula && (
                  <button
                    onClick={() => setSearchCedula('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                onClick={loadNotificaciones}
                variant="outline"
                className="border-gray-600 text-white hover:bg-gray-700"
              >
                Actualizar
              </Button>
            </div>
            {searchCedula && (
              <p className="text-sm text-gray-400 mt-2">
                Mostrando {notificacionesFiltradas.length} de {notificaciones.length} notificaciones
              </p>
            )}
          </CardContent>
        </Card>

        {/* Lista de Notificaciones */}
        {notificacionesFiltradas.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="py-16 text-center">
              <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchCedula ? 'No hay resultados' : 'No hay notificaciones pendientes'}
              </h3>
              <p className="text-gray-400">
                {searchCedula 
                  ? 'Intenta con otro término de búsqueda'
                  : 'Todas las notificaciones han sido atendidas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-400">
                Ordenadas por antigüedad (las más antiguas primero)
              </p>
            </div>

            {notificacionesFiltradas.map((notif) => (
              <Card
                key={notif.id}
                className="bg-gray-800/50 border-gray-700 hover:border-blue-500/50 transition-all cursor-pointer group"
                onClick={() => handleNotificationClick(notif.id, notif.expediente_id)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Indicador de no leída */}
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-1.5 flex-shrink-0 group-hover:scale-110 transition-transform"></div>
                    
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-white group-hover:text-yellow-500 transition-colors">
                          {notif.titulo}
                        </h3>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatFecha(notif.created_at)}
                        </span>
                      </div>
                      
                      <p className="text-gray-400 mb-3 leading-relaxed">
                        {notif.mensaje}
                      </p>
                      
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-blue-400 border-blue-500/30">
                          Exp. {notif.expediente_numero}
                        </Badge>
                        
                        {notif.tipo && (
                          <Badge variant="outline" className="text-purple-400 border-purple-500/30">
                            {notif.tipo === 'documento_subido' && '📄 Documento'}
                            {notif.tipo === 'formulario_completado' && '📋 Formulario'}
                            {notif.tipo === 'registro_vip' && '🌟 VIP'}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Indicador de acción */}
                    <div className="text-gray-600 group-hover:text-blue-500 transition-colors">
                      <ArrowLeft className="w-5 h-5 rotate-180" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
